import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, getToken } from "../lib/api";
import { compressImage } from "../lib/imageCompression";
import { AppShell } from "../components/AppShell";
import ShopifyImportModal from "../components/ShopifyImportModal";
import PushToMyntraButton from "../components/PushToMyntraButton";
import { RechargeModal } from "../components/RechargeCredits";
import { TutorialSidebar, TutorialVideoModal, StepsSidebar } from "../components/TutorialSupport";
import {
  FileSpreadsheet,
  ImagePlus,
  Sparkles,
  CheckCircle2,
  Loader2,
  Download,
  ChevronRight,
  Settings2,
  X,
  Search,
  Folder,
  Ruler,
  Package,
  BookmarkPlus,
  Bookmark,
  Trash2,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";

/* ───────── constants ───────── */
const IMAGE_COLS = [
  "Front Image",
  "Side Image",
  "Back Image",
  "Detail Angle",
  "Look Shot Image",
  "Additional Image 1",
  "Additional Image 2",
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ───────── helpers ───────── */
const blankSKU = () => ({
  _rowId: crypto.randomUUID(),
  vendorSkuCode: "",
  vendorArticleName: "",
  productDisplayName: "",
  brandSize: "",
  standardSize: "",
  prominentColour: "",
  mrp: "",
  isp: "",
  productDetails: "",
  styleNote: "",
  images: {},
});

function slug(s) {
  return (s || "").replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "").toUpperCase().slice(0, 16) || "SKU";
}

/* ───────── step card shell ───────── */

/* ───────── footwear size chart (Myntra) ─────────
   UK shoe sizes shipped in the Myntra Footwear masterdata (UK 1–13).
   US = UK + 1, EURO = UK + 33, foot length from Myntra's published chart
   (UK3→22.7 cm … UK8→26.0 cm), extrapolated ±0.33 cm per half size. */
const SHOE_SIZES = [
  "1", "1.5", "2", "2.5", "3", "3.5", "4", "4.5", "5", "5.5",
  "6", "6.5", "7", "7.5", "8", "8.5", "9", "9.5", "10", "10.5",
  "11", "11.5", "12", "12.5", "13",
];
const UK_FOOT_LENGTH = {
  "1": "21.4", "1.5": "21.7", "2": "22.0", "2.5": "22.4", "3": "22.7",
  "3.5": "23.0", "4": "23.3", "4.5": "23.7", "5": "24.0", "5.5": "24.3",
  "6": "24.7", "6.5": "25.0", "7": "25.3", "7.5": "25.7", "8": "26.0",
  "8.5": "26.3", "9": "26.7", "9.5": "27.0", "10": "27.3", "10.5": "27.7",
  "11": "28.0", "11.5": "28.3", "12": "28.7", "12.5": "29.0", "13": "29.3",
};
function shoeDefaults(uk) {
  const n = parseFloat(uk);
  if (!Number.isFinite(n)) return {};
  const fmt = (x) => (Number.isInteger(x) ? String(x) : x.toFixed(1));
  return {
    usSize: fmt(n + 1),
    euroSize: fmt(n + 33),
    toFitFootLength: UK_FOOT_LENGTH[uk] || "",
  };
}

function StepCard({ step, title, subtitle, done, locked, children }) {
  return (
    <div
      className={`rounded-2xl border transition-all ${
        locked
          ? "bg-white/3 border-white/5 opacity-50 pointer-events-none"
          : done
          ? "bg-emerald-500/5 border-emerald-500/30"
          : "bg-white/5 border-white/10"
      }`}
      data-testid={`step-${step}`}
    >
      <div className="p-5 flex items-start gap-4">
        <div
          className={`shrink-0 w-9 h-9 rounded-full grid place-items-center text-sm font-bold ${
            done
              ? "bg-emerald-500"
              : locked
              ? "bg-white/10 text-white/40"
              : "bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] shadow-md shadow-indigo-500/30"
          }`}
          style={done || !locked ? { color: "#FFFFFF" } : undefined}
        >
          {done ? <CheckCircle2 size={18} /> : step}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-base font-bold text-white">{title}</div>
          {subtitle && (
            <div className="text-[11px] text-white/55 mt-0.5">{subtitle}</div>
          )}
          <div className="mt-4">{children}</div>
        </div>
      </div>
    </div>
  );
}

/* ───────── searchable category picker (185 cats) ───────── */
function CategoryPicker({ value, groups, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const all = useMemo(() => {
    const out = [];
    Object.entries(groups || {}).forEach(([family, cats]) => {
      cats.forEach((c) => out.push({ cat: c, family }));
    });
    return out;
  }, [groups]);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((x) => x.cat.toLowerCase().includes(needle));
  }, [q, all]);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors"
        style={{ background: "#FFFFFF", border: "1.5px solid #C7C9D9", color: "#282C3F", boxShadow: "0 1px 3px rgba(15,23,42,0.06)" }}
        data-testid="category-picker-btn"
      >
        <span className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: "#4F46E5" }} />
          {value || "Pick category"}
        </span>
        <ChevronRight
          size={14}
          className={`transition-transform ${open ? "rotate-90" : ""}`}
        />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 left-0 right-0 max-h-80 overflow-y-auto rounded-lg bg-[#1A0F11] border border-white/15 shadow-2xl">
          <div className="sticky top-0 p-2 bg-[#1A0F11] border-b border-white/10">
            <div className="flex items-center gap-2 px-2 py-1 bg-black/40 rounded-md">
              <Search size={12} className="text-white/40" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search 185 categories…"
                autoFocus
                className="flex-1 bg-transparent text-xs text-white placeholder:text-white/40 focus:outline-none"
                data-testid="category-search"
              />
            </div>
          </div>
          {filtered.length === 0 && (
            <div className="px-3 py-4 text-xs text-white/40">No match</div>
          )}
          {filtered.map((x) => (
            <button
              key={x.cat}
              onClick={() => {
                onChange(x.cat);
                setOpen(false);
                setQ("");
              }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex items-center justify-between ${
                value === x.cat ? "bg-[#4F46E5]/10 text-white" : "text-white/80"
              }`}
              data-testid={`category-opt-${x.cat}`}
            >
              <span>{x.cat}</span>
              <span className="text-[9px] uppercase tracking-widest text-white/30">
                {x.family}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ───────── photo upload zone (single files OR whole folder) ───────── */
function PhotoUploadZone({ assets, onAdd, onRemove, onSaveAndAddNew, pendingCount, onShopifyClick }) {
  const filesRef = useRef(null);
  const folderRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  async function handleFiles(files) {
    const list = Array.from(files || []).filter((f) =>
      /^image\/(jpeg|png|webp)/.test(f.type)
    );
    if (!list.length) {
      toast.error("No JPG/PNG/WEBP images found");
      return;
    }
    const free = IMAGE_COLS.length - Object.keys(assets).length;
    if (free <= 0) {
      toast.error(`All ${IMAGE_COLS.length} image slots already filled — click "Save & add another product" to start a new one`);
      return;
    }
    const accepted = list.slice(0, free);
    if (accepted.length < list.length) {
      toast.message(
        `Only first ${accepted.length} images used (max ${IMAGE_COLS.length} per SKU)`
      );
    }
    setBusy(true);
    setProgress({ done: 0, total: accepted.length });
    const slots = IMAGE_COLS.filter((c) => !assets[c]);
    const updates = {};
    for (let i = 0; i < accepted.length; i++) {
      const original = accepted[i];
      const slot = slots[i];
      if (!slot) break;
      // Always compress in-browser first. iPhone 14MP photos go from
      // ~10 MB → ~500 KB, eliminating the 520 Cloudflare timeout we hit
      // when raw camera shots travelled over 3G/4G uploads.
      const f = await compressImage(original);
      // Local preview URL — always renders, even if the backend image
      // route is slow or the file gets pruned from storage later.
      const localUrl = URL.createObjectURL(f);
      try {
        const fd = new FormData();
        fd.append("image", f);
        const { data } = await api.post(
          "/seller/auto-listing/upload-image",
          fd,
          { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 }
        );
        updates[slot] = {
          url: data.url,
          relative_url: data.relative_url,
          asset_id: data.asset_id,
          filename: f.name,
          local_url: localUrl,
          // Keep the compressed File object in memory so AI Auto-Fill
          // doesn't have to re-fetch it from the backend (which would
          // 404 if the production pod recycled between upload and
          // analyze and wiped the ephemeral disk).
          local_file: f,
        };
      } catch (e) {
        toast.error(errMsg(e, `Failed to upload ${original.name}`));
      }
      setProgress({ done: i + 1, total: accepted.length });
    }
    onAdd(updates);
    setBusy(false);
    setProgress({ done: 0, total: 0 });
  }

  const tiles = IMAGE_COLS.map((col) => ({
    col,
    asset: assets[col],
  })).filter((t) => t.asset);

  const freeCount = IMAGE_COLS.length - tiles.length;
  const hasUploaded = tiles.length > 0;
  const slotsFull = freeCount === 0;

  // When the current product's slots are full, the folder button becomes
  // "Save & add another product" — snapshots the current product into the
  // bulk queue, resets the page, and opens the folder picker for the next.
  async function handleFolderClick() {
    if (slotsFull && onSaveAndAddNew) {
      const ok = await onSaveAndAddNew();
      if (!ok) return; // validation failed; main page already toasted
      // After the queue commit, the page state is reset → re-open picker.
      requestAnimationFrame(() => folderRef.current?.click());
      return;
    }
    folderRef.current?.click();
  }

  async function handleSaveAndAdd() {
    if (!onSaveAndAddNew) return;
    const ok = await onSaveAndAddNew();
    if (!ok) return;
    requestAnimationFrame(() => folderRef.current?.click());
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <button
          onClick={() => filesRef.current?.click()}
          disabled={busy || freeCount === 0}
          className="flex items-center justify-center gap-2 px-4 py-4 rounded-xl border-2 border-dashed border-[#4F46E5]/40 bg-[#4F46E5]/5 hover:bg-[#4F46E5]/10 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-bold"
          style={{ color: "#1E1B4B" }}
          data-testid="upload-photos-btn"
        >
          {busy ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Uploading {progress.done}/{progress.total}…
            </>
          ) : (
            <>
              <ImagePlus size={16} /> {hasUploaded ? "Pick more files" : "Pick files"}
            </>
          )}
        </button>
        <button
          onClick={hasUploaded && onSaveAndAddNew ? handleSaveAndAdd : handleFolderClick}
          disabled={busy}
          className={`flex items-center justify-center gap-2 px-4 py-4 rounded-xl border-2 border-dashed disabled:opacity-40 disabled:cursor-not-allowed text-sm font-bold ${
            hasUploaded && onSaveAndAddNew
              ? "border-emerald-500/50 bg-emerald-500/10 hover:bg-emerald-500/20"
              : "border-[#94969F]/40 bg-[#F8F8FA] hover:bg-[#EDEDF1]"
          }`}
          style={{ color: hasUploaded && onSaveAndAddNew ? "#064E3B" : "#1E1B4B" }}
          data-testid="upload-folder-btn"
        >
          {hasUploaded && onSaveAndAddNew ? (
            <>
              <CheckCircle2 size={16} /> Save &amp; add another product
            </>
          ) : (
            <>
              <Folder size={16} />{" "}
              {hasUploaded ? "Pick another folder" : "Pick whole folder"}
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onShopifyClick}
          disabled={busy}
          className="flex items-center justify-center gap-2 px-4 py-4 rounded-xl border-2 border-dashed bg-[#ECFDF5] hover:bg-[#D1FAE5] disabled:opacity-40 disabled:cursor-not-allowed text-sm font-bold"
          style={{ color: "#064E3B", borderColor: "#10B981" }}
          data-testid="upload-shopify-btn"
          title="Import products from a Shopify store URL or CSV export"
        >
          <Sparkles size={16} style={{ color: "#047857" }} />
          <span>Import from Shopify</span>
        </button>
      </div>
      {slotsFull && !onSaveAndAddNew && (
        <div className="text-[11px] text-emerald-300/80 flex items-center gap-1">
          <CheckCircle2 size={11} /> All {IMAGE_COLS.length} image slots filled.
        </div>
      )}
      <input
        ref={filesRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      <input
        ref={folderRef}
        type="file"
        webkitdirectory=""
        directory=""
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      {tiles.length > 0 && (
        <div className="grid grid-cols-4 gap-2">
          {tiles.map(({ col, asset }) => (
            <div
              key={col}
              className="relative rounded-md overflow-hidden aspect-[3/4] bg-black/30 border border-white/10"
            >
              <img
                src={asset.local_url || `${BACKEND_URL}${asset.relative_url}`}
                alt={col}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Fallback to backend URL if the local blob was revoked
                  // (e.g., after a page reload or template-load).
                  const backendSrc = `${BACKEND_URL}${asset.relative_url}`;
                  if (e.currentTarget.src !== backendSrc) {
                    e.currentTarget.src = backendSrc;
                  }
                }}
              />
              <button
                onClick={() => onRemove(col)}
                className="absolute top-1 right-1 p-0.5 rounded bg-black/70 hover:bg-black text-white/80"
                data-testid={`remove-img-${col.toLowerCase().replace(/\s/g, "-")}`}
              >
                <X size={10} />
              </button>
              <div className="absolute bottom-0 left-0 right-0 px-1.5 py-0.5 bg-black/70 text-[9px] font-semibold text-white truncate">
                {col}
              </div>
            </div>
          ))}
          {/* Empty slots stay VISIBLE so sellers (incl. free-demo users) know
              they can add Image 2, 3, 4… — click any to open the picker. */}
          {hasUploaded && !slotsFull &&
            IMAGE_COLS.filter((c) => !assets[c]).map((col, i) => (
              <button
                key={col}
                onClick={() => filesRef.current?.click()}
                disabled={busy}
                className="rounded-md aspect-[3/4] flex flex-col items-center justify-center gap-1 border-2 border-dashed transition-colors"
                style={{ borderColor: "#C7C9D9", background: "#FAFAFC", color: "#94969F" }}
                data-testid={`empty-slot-${col.toLowerCase().replace(/\s/g, "-")}`}
                title={`Add ${col}`}
              >
                <ImagePlus size={16} />
                <span className="text-[9px] font-bold text-center px-1 leading-tight">
                  {i === 0 ? `+ Add ${col}` : col}
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

/* ───────── credit usage badge ─────────
 * Renders next to the page title so the seller always knows
 * how many of their plan's listings they have left.
 */
function CreditUsageBadge({ info }) {
  const TIER_LABEL = { starter: "Starter", pro: "Pro", business: "Business" };
  const PLAN_TOTAL = { starter: 100, pro: 500, business: 1000 };

  // Trial users — show trial counter
  if (info.account_type === "trial" || (info.plan_tier !== "starter" && info.plan_tier !== "pro" && info.plan_tier !== "business" && info.listings_credits === 0)) {
    const used = info.trial_listings_used;
    const total = info.trial_listings_limit;
    const remaining = Math.max(0, total - used);
    return (
      <div
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border"
        style={{ borderColor: "#FFD3DD", background: "#FFF1F5" }}
        data-testid="credit-usage-badge"
      >
        <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#FF3F6C" }}>
          Free trial
        </span>
        <span className="font-mono text-sm font-extrabold" style={{ color: remaining === 0 ? "#DC2626" : "#0B0C12" }}>
          {used}<span className="opacity-50"> / </span>{total}
        </span>
        <span className="text-[10px]" style={{ color: "#535665" }}>used</span>
      </div>
    );
  }

  const tier = info.plan_tier;
  const total = info.listings_credits + info.listings_credits_used || PLAN_TOTAL[tier] || 0;
  const used = info.listings_credits_used;
  const remaining = Math.max(0, info.listings_credits);
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const tierLabel = TIER_LABEL[tier] || "Plan";
  const danger = remaining === 0;
  const warn = !danger && remaining / Math.max(total, 1) <= 0.1; // <=10% left

  return (
    <div
      className="inline-flex items-center gap-3 px-3 py-1.5 rounded-lg border"
      style={{
        borderColor: danger ? "#FCA5A5" : warn ? "#FED7AA" : "#C7D2FE",
        background:    danger ? "#FEF2F2" : warn ? "#FFF7ED" : "#EEF2FF",
      }}
      data-testid="credit-usage-badge"
    >
      <div className="flex flex-col leading-tight">
        <span className="text-[9px] uppercase tracking-widest font-bold" style={{ color: danger ? "#DC2626" : warn ? "#C2410C" : "#4338CA" }}>
          {tierLabel} Plan
        </span>
        <span className="font-mono text-sm font-extrabold" style={{ color: danger ? "#DC2626" : "#0B0C12" }}>
          {used}<span className="opacity-50"> / </span>{total}
        </span>
      </div>
      <div className="flex flex-col items-end gap-0.5 min-w-[100px]">
        <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: "#E5E7EB" }}>
          <div
            className="h-full transition-all"
            style={{
              width: `${pct}%`,
              background: danger
                ? "linear-gradient(90deg, #DC2626, #EF4444)"
                : warn
                ? "linear-gradient(90deg, #F59E0B, #FB923C)"
                : "linear-gradient(90deg, #4F46E5, #7C3AED)",
            }}
          />
        </div>
        <span className="text-[10px] font-bold" style={{ color: danger ? "#DC2626" : warn ? "#C2410C" : "#4338CA" }}>
          {remaining} listings left
        </span>
        {(danger || warn) && (
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("ecai:show-recharge"))}
            className="ml-1 px-2 py-0.5 rounded-md bg-[#03A685] text-white text-[10px] font-bold hover:bg-[#02936F]"
            data-testid="quota-recharge-btn"
          >
            Recharge
          </button>
        )}
      </div>
    </div>
  );
}

/* ───────── main page ───────── */
export default function AutoListingQuick() {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [common, setCommon] = useState({});
  const [activeSku, setActiveSku] = useState(blankSKU());
  const [sizes, setSizes] = useState([]); // selected size strings
  // Seller-supplied hint passed to AI Auto-Fill so Claude doesn't pick the
  // wrong category from a noisy photo. Free-form: "Kurti", "Anarkali Dress",
  // "Hipster Brief Pack of 3", "Cotton Saree" etc.
  const [productHint, setProductHint] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiElapsed, setAiElapsed] = useState(0);
  const [aiDone, setAiDone] = useState(false);
  const [aiCount, setAiCount] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [genStatus, setGenStatus] = useState("");
  const [pendingJobId, setPendingJobId] = useState(null);
  // Persist the last generate() payload so the Retry button can rerun
  // the exact same inputs (no lost work). Cleared on successful generate.
  const [lastGenPayload, setLastGenPayload] = useState(null);
  const [result, setResult] = useState(null);
  const [showDefaults, setShowDefaults] = useState(false);

  // Bulk queue — each pending product is a self-contained snapshot that
  // will be flattened into per-size SKU rows at Generate time.
  const [pendingProducts, setPendingProducts] = useState([]);

  // Shopify import modal — opens from PhotoUploadZone's third button.
  const [shopifyOpen, setShopifyOpen] = useState(false);

  // Self-serve credits recharge (₹10 = 1 listing) — auto-opens when the
  // seller's credits hit 0 right after a generate, plus manual trigger.
  const [showRecharge, setShowRecharge] = useState(false);
  useEffect(() => {
    const fn = () => setShowRecharge(true);
    window.addEventListener("ecai:show-recharge", fn);
    return () => window.removeEventListener("ecai:show-recharge", fn);
  }, []);

  // Tutorial video popup — auto-opens ONCE for trial (Try It Free) users
  // right after login; manual replay via the right-side support card.
  const [showTutorial, setShowTutorial] = useState(false);
  useEffect(() => {
    if (localStorage.getItem("ecai_tutorial_seen")) return;
    api.get("/auth/me").then(({ data }) => {
      if (data.account_type === "trial") {
        setShowTutorial(true);
        localStorage.setItem("ecai_tutorial_seen", "1");
      }
    }).catch(() => {});
  }, []);

  // Live credit summary so the seller can see how many listings they've
  // used out of their plan (100 / 500 / 1000). Refreshed on mount and after
  // every successful Generate.
  const [creditInfo, setCreditInfo] = useState(null);
  const refreshCreditInfo = React.useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setCreditInfo({
        plan_tier: data.plan_tier || "trial",
        listings_credits: Number(data.listings_credits || 0),
        listings_credits_used: Number(data.listings_credits_used || 0),
        trial_listings_used: Number(data.trial_listings_used || 0),
        trial_listings_limit: Number(data.trial_listings_limit || 1),
        account_type: data.account_type,
      });
    } catch {
      /* swallow — not critical for the form */
    }
  }, []);
  useEffect(() => { refreshCreditInfo(); }, [refreshCreditInfo]);

  useEffect(() => {
    (async () => {
      try {
        const [m, d] = await Promise.all([
          api.get("/seller/auto-listing/meta"),
          api.get("/seller/compliance-defaults").catch(() => ({ data: {} })),
        ]);
        setMeta(m.data);
        setCommon((c) => ({
          // Pre-select the seller's LAST-USED category (category memory);
          // brand-new sellers still pick manually.
          articleType: m.data.last_category || "",
          countryOfOrigin: "India",
          ...d.data,
          ...c,
        }));
      } catch (e) {
        toast.error(errMsg(e, "Failed to load template"));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* derive */
  const photoCount = Object.keys(activeSku.images || {}).length;
  const frontImage = activeSku.images?.["Front Image"];

  // Smart Pre-Fill: as soon as Front Image + brand are both known, silently
  // start the AI analysis in the background so "AI Auto-Fill" feels instant
  // (server dedupes the job, so no double LLM spend).
  const prefillKeyRef = useRef("");
  useEffect(() => {
    const brand = (common.brand || "").trim();
    if (!frontImage?.asset_id || !brand) return;
    const key = `${frontImage.asset_id}|${brand.toLowerCase()}|${productHint.trim().toLowerCase()}`;
    if (prefillKeyRef.current === key) return;
    const t = setTimeout(() => {
      prefillKeyRef.current = key;
      const fd = new FormData();
      fd.append("brand", brand);
      if (common.articleType) fd.append("category", common.articleType);
      if (productHint.trim()) fd.append("product_hint", productHint.trim());
      fd.append("asset_id", frontImage.asset_id);
      api
        .post("/seller/auto-listing/analyze-image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 30000,
        })
        .catch(() => {});
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [frontImage?.asset_id, common.brand, productHint]);
  // BPC (Beauty / Personal Care / Nails / Furniture) categories don't have
  // garment sizes — they ship in volume (ml, g, oz) or as single units. We
  // detect this from the resolved source template name so adding new BPC
  // categories doesn't need a code change.
  const isBPCCategory = useMemo(() => {
    const cat = common.articleType;
    if (!cat || !meta?.category_sources) return false;
    const fname = meta.category_sources[cat] || "";
    return /Beauty|Nails|Furniture|FaceMakeup|Makeup|Serum|CleansingTools/i.test(fname);
  }, [common.articleType, meta]);

  const step1Done = photoCount > 0;
  const step2Done = aiDone;
  const step3Done = !!(common.brand && activeSku.mrp);
  // BPC keeps the size step but with ML/gm volume buckets instead of
  // garment letter sizes. Seller still picks at least one volume.
  const step4Done = sizes.length > 0;
  const allDone = step1Done && step2Done && step3Done && step4Done;

  // Fallback list used when /meta is slow/unavailable. Covers every
  // letter size Myntra ships in its master Size List.
  const DEFAULT_LETTER_SIZES = useMemo(
    () => [
      "XXS", "XS", "S", "M", "L", "XL", "XXL",
      "3XL", "4XL", "5XL", "6XL",
      "Free Size", "Onesize",
    ],
    []
  );

  const sizeOptions = useMemo(() => {
    if (!meta) return DEFAULT_LETTER_SIZES;
    const f = meta.per_sku_fields.find((x) => x.key === "standardSize");
    const opts = f?.options || [];
    return opts.length ? opts : DEFAULT_LETTER_SIZES;
  }, [meta, DEFAULT_LETTER_SIZES]);

  // Shirts / formal wear use numeric chest sizes (32-46). Map each to the
  // closest standard size letter so the Excel still has a standardSize.
  const SHIRT_SIZES = ["26", "28", "30", "32", "34", "36", "38", "40", "42", "44", "46"];
  // Footwear: UK shoe sizes (Myntra masterdata ships UK 1–13.5). US, EURO
  // and To-Fit-Foot-Length auto-map from the Myntra size chart below and
  // stay editable per size in the measurements drawer.
  // Beauty / Personal Care SKUs come in ML or gm volumes — never in
  // garment sizes. List below mirrors the most-used Myntra Beauty
  // volume buckets so sellers tick the right pack size for their SKU.
  const BPC_SIZES = [
    "0-50 ML", "50-80 ML", "80-90 ML", "90-100 ML", "100-150 ML",
    "150-180 ML", "200-220 ML", "220-240 ML", "240-260 ML", "400-500 ML",
    "0-50gm", "50-60gm", "60-75gm", "75-100gm", "100-125gm",
    "200-250gm", "300-350gm",
    "Onesize", "Pack",
  ];
  const isShirtCategory = useMemo(() => {
    const c = (common.articleType || "").toLowerCase();
    return /shirt|kurta|blazer|suit|jacket|trouser|jean/.test(c) &&
      !/tshirt|t-shirt/.test(c);
  }, [common.articleType]);
  // T-shirts / sweatshirts use letter sizes but their measurements are
  // chest-based (not bust). Drives the measurements drawer profile.
  const isChestMeasureCategory = useMemo(() => {
    const c = (common.articleType || "").toLowerCase();
    return /tshirt|t-shirt|sweatshirt|polo|vest/.test(c);
  }, [common.articleType]);
  // Footwear categories (Casual/Sports/Formal Shoes, Sandals, Heels, Flats,
  // Flip Flops, Boots) use UK shoe sizes + US/EURO/foot-length columns.
  const isShoeCategory = useMemo(() => {
    const cat = common.articleType || "";
    if (!/shoe|sandal|heel|flat|flip flop|boot/i.test(cat)) return false;
    const fname = meta?.category_sources?.[cat];
    return fname ? /Footwear/i.test(fname) : true;
  }, [common.articleType, meta]);
  const [sizeMode, setSizeMode] = useState("letters"); // "letters" | "shirt" | "bpc" | "shoe"
  useEffect(() => {
    if (isBPCCategory) setSizeMode("bpc");
    else if (isShoeCategory) setSizeMode("shoe");
    else if (isShirtCategory) setSizeMode("shirt");
    else setSizeMode("letters");
    // Letter / shirt sizes are invalid for shoes — drop stale selections
    // when the seller switches into a footwear category.
    if (isShoeCategory) {
      setSizes((cur) => cur.filter((s) => SHOE_SIZES.includes(s)));
    }
  }, [isShirtCategory, isBPCCategory, isShoeCategory]); // eslint-disable-line react-hooks/exhaustive-deps
  // Beauty categories: pull the EXACT Standard Size options from the
  // category's Myntra template masterdata (hardcoded buckets missed sizes
  // like "3-3.5 ML" — user report).
  const [bpcSizeOpts, setBpcSizeOpts] = useState(null);
  useEffect(() => {
    const cat = (common.articleType || "").trim();
    if (!cat || !isBPCCategory) {
      setBpcSizeOpts(null);
      return;
    }
    api
      .get(`/seller/auto-listing/category-sizes?category=${encodeURIComponent(cat)}`)
      .then(({ data }) =>
        setBpcSizeOpts(data.standard_sizes?.length ? data.standard_sizes : null)
      )
      .catch(() => setBpcSizeOpts(null));
    // eslint-disable-next-line
  }, [common.articleType, isBPCCategory]);

  const currentSizeList =
    sizeMode === "bpc" ? (bpcSizeOpts || BPC_SIZES) :
    sizeMode === "shoe" ? SHOE_SIZES :
    sizeMode === "shirt" ? SHIRT_SIZES :
    sizeOptions;

  // Pre-warm the backend template cache for the selected category. Fires
  // the moment the user picks a category so by the time they click
  // "Generate Excel" the trimmed-bytes are already cached → generate
  // returns in ~3 s instead of ~28 s (which was tripping the Cloudflare
  // 30 s ingress timeout and showing as a 520 on production).
  useEffect(() => {
    const cat = (common.articleType || "").trim();
    if (!cat) return;
    api
      .post("/seller/auto-listing/warm", { category: cat })
      .catch(() => {
        // Best-effort — warm failure shouldn't disrupt the user flow.
      });
  }, [common.articleType]);

  // Auto-download: the moment the Excel is ready, start the browser
  // download automatically — no extra click needed.
  useEffect(() => {
    if (!result?.download_url) return;
    const a = document.createElement("a");
    a.href = `${BACKEND_URL}${result.download_url}?token=${getToken()}`;
    a.download = result.filename || "myntra_listing.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    // eslint-disable-next-line
  }, [result?.download_url]);

  // Per-size measurements: { "M": { measureChest: "38", measureToFitChest: "40", ... } }
  const [sizeMeasures, setSizeMeasures] = useState({});
  const [showMeasures, setShowMeasures] = useState(false);

  // Pack/combo toggle — also toggled automatically by AI when is_pack=true
  const [isPack, setIsPack] = useState(false);

  // AI-returned SEO fields (editable)
  const [seoTags, setSeoTags] = useState("");

  /* actions */
  function addImages(updates) {
    setActiveSku((s) => ({ ...s, images: { ...(s.images || {}), ...updates } }));
  }
  function removeImage(col) {
    setActiveSku((s) => {
      const next = { ...(s.images || {}) };
      delete next[col];
      return { ...s, images: next };
    });
  }

  /* ─── BULK QUEUE ───────────────────────────────────────────────────── */
  // Validate the current product has enough info to queue (or generate).
  // Brand + sizes + MRP are needed; common compliance fields are inherited
  // across the queue so we don't re-prompt for them on every product.
  function validateCurrentProductForQueue() {
    if (!Object.keys(activeSku.images || {}).length) {
      return "Upload at least one image before saving this product";
    }
    if (!common.brand?.trim()) return "Brand is required (step 2)";
    if (!activeSku.mrp) return "MRP is required (step 2)";
    if (!sizes.length) return "Pick at least one size (step 4)";
    return null;
  }

  // Snapshot the current product → push into the bulk queue, then reset
  // the page so the user can immediately upload the next folder.
  async function commitCurrentToQueue() {
    const err = validateCurrentProductForQueue();
    if (err) {
      toast.error(err);
      return false;
    }
    const snapshot = {
      _id: crypto.randomUUID(),
      activeSku: { ...activeSku, _rowId: crypto.randomUUID() },
      sizes: [...sizes],
      sizeMeasures: { ...sizeMeasures },
      sizeMode,
      isPack,
      seoTags,
      // Per-product common-overrides we want to keep separate so each
      // product can have its own colour/fabric/etc. Only fields that vary
      // per design are snapshotted; compliance defaults (manufacturer,
      // packer, HSN…) stay on the shared `common` state.
      perProductCommon: {
        articleType: common.articleType,
        fabric: common.fabric,
        sleeveLength: common.sleeveLength,
        neck: common.neck,
        pattern: common.pattern,
        occasion: common.occasion,
        washCare: common.washCare,
        shape: common.shape,
        hemline: common.hemline,
        materialCareDescription: common.materialCareDescription,
        sizeAndFitDescription: common.sizeAndFitDescription,
      },
    };
    setPendingProducts((q) => [...q, snapshot]);
    // Reset per-product state. Compliance defaults (brand, manufacturer,
    // packer, HSN, country, ageGroup) stay so the seller doesn't re-type.
    //
    // Bulk-listing UX: preserve `sizes` AND `sizeMeasures` for the NEXT
    // product so the seller doesn't have to re-pick S/M/L/XL and
    // re-enter chest/waist/length for every garment. They can still
    // change them if the next product has different sizing.
    setActiveSku(blankSKU());
    setIsPack(false);
    setSeoTags("");
    setAiDone(false);
    setAiCount(0);
    setResult(null);
    toast.success(
      `Product saved — ${pendingProducts.length + 1} queued. Sizes & measurements carried over to the next product.`
    );
    return true;
  }

  function removeFromQueue(id) {
    setPendingProducts((q) => q.filter((p) => p._id !== id));
  }

  // Convert imported Shopify products → pendingProducts snapshots so the
  // seller can immediately click "Generate Excel" without any further
  // upload or AI step. Each Shopify product becomes one queued product.
  function ingestShopifyProducts(imported) {
    if (!imported || !imported.length) return;
    const snapshots = imported.map((p) => ({
      _id: crypto.randomUUID(),
      activeSku: {
        _rowId: crypto.randomUUID(),
        vendorSkuCode: p.vendorSkuCode || "",
        vendorArticleName: p.vendorArticleName || p.productDisplayName || "",
        productDisplayName: p.productDisplayName || "",
        brandSize: (p.sizes && p.sizes[0]) || "Free Size",
        standardSize: (p.sizes && p.sizes[0]) || "Free Size",
        prominentColour: p.prominentColour || "",
        mrp: p.mrp || "",
        isp: p.isp || p.mrp || "",
        productDetails: p.productDetails || "",
        styleNote: p.styleNote || "",
        images: p.images || {},
      },
      sizes: (p.sizes && p.sizes.length) ? p.sizes : ["Free Size"],
      sizeMeasures: {},
      sizeMode: "letter",
      isPack: false,
      seoTags: p.tags || "",
      perProductCommon: {
        articleType: p.articleType || common.articleType || "Kurtis",
        fabric: p.fabric || "",
        sleeveLength: p.sleeveLength || "",
        neck: p.neck || "",
        pattern: p.pattern || "",
        occasion: p.occasion || "",
        washCare: p.washCare || "",
        shape: "",
        hemline: "",
        materialCareDescription: "",
        sizeAndFitDescription: "",
      },
    }));
    setPendingProducts((q) => [...q, ...snapshots]);
    // Set common.articleType from the first imported product so the
    // Generate flow's category check passes without manual tweaking.
    if (snapshots[0]?.perProductCommon?.articleType) {
      setCommon((c) => ({ ...c, articleType: snapshots[0].perProductCommon.articleType }));
    }
    // Refresh credit info so the badge reflects the new pending count.
    refreshCreditInfo();
  }

  async function runAi() {
    if (aiBusy) {
      // Hard guard — runAi can be triggered by a double-click or a stale
      // React effect re-fire. Both code paths would race on the same
      // frontImage Response body and trigger
      // "Failed to execute 'blob' on 'Response': body stream already read".
      return;
    }
    if (!frontImage) {
      toast.error("Upload the Front Image first");
      return;
    }
    if (!common.brand?.trim()) {
      toast.error("Type the brand name first (step 2)");
      return;
    }
    setAiBusy(true);
    setAiElapsed(0);
    // Tick a friendly elapsed counter so the user knows the AI is still
    // working and the UI hasn't frozen.
    const elapsedTimer = setInterval(() => setAiElapsed((s) => s + 1), 1000);
    try {
      // 1) Kick off the AI Vision job.
      //
      //    Fast path: if the image is already on the backend (we kept
      //    the asset_id from the upload-image response), send a tiny
      //    `asset_id` form field instead of re-uploading 5+ MB of bytes.
      //    The big re-upload over a slow Cloudflare hop was the root
      //    cause of the "524 origin timeout" error production sellers
      //    hit on AI Auto-Fill.
      //
      //    Fallback (re-upload bytes): only used if we somehow don't
      //    have an asset_id — e.g. an older session where the upload
      //    happened on a pre-fix build.
      const fd = new FormData();
      fd.append("brand", common.brand.trim());
      if (common.articleType) {
        fd.append("category", common.articleType);
      }
      if (productHint.trim()) {
        fd.append("product_hint", productHint.trim());
      }
      if (frontImage.asset_id) {
        fd.append("asset_id", frontImage.asset_id);
      } else {
        let blob;
        if (frontImage.local_file) {
          blob = frontImage.local_file;
        } else {
          const apiPath = frontImage.relative_url.replace(/^\/api/, "");
          try {
            const imgResp = await api.get(apiPath, {
              responseType: "blob",
              timeout: 20000,
            });
            blob = imgResp.data;
          } catch (fetchErr) {
            throw new Error(
              "Photo was lost from server cache — please re-upload the front image and try AI Auto-Fill again."
            );
          }
        }
        fd.append("image", blob, frontImage.filename || "front.jpg");
      }

      // 2) Kick off the background job — may return inline-done in <8 s
      //    thanks to the server-side short-circuit on Claude Haiku 4.5
      //    + 320 px image clamp + single-attempt path.
      const { data: startResp } = await api.post(
        "/seller/auto-listing/analyze-image",
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 35000 }
      );
      const jobId = startResp.job_id;
      if (!jobId) {
        throw new Error("Server didn't return a job ID");
      }

      // Fast path: server already finished within its inline-wait window.
      let attrs = null;
      if (startResp.status === "done" && startResp.attributes) {
        attrs = startResp.attributes;
      } else if (startResp.status === "failed") {
        throw new Error(startResp.error || "AI analysis failed — try again");
      } else {
        // Slow path: long-poll the job. `?wait=4` holds the connection
        // server-side until the worker finishes or 4s elapse — saves
        // round-trips vs. a 400ms client loop.
        const MAX_TRIES = 18; // 18 × 4s = 72s ceiling (4-tier AI waterfall)
        for (let i = 0; i < MAX_TRIES && !attrs; i++) {
          const { data: poll } = await api.get(
            `/seller/auto-listing/analyze-image/job/${jobId}?wait=4`,
            { timeout: 8000 }
          );
          if (poll.status === "done") {
            attrs = poll.attributes || {};
            break;
          }
          if (poll.status === "failed") {
            throw new Error(poll.error || "AI analysis failed — try again");
          }
        }
      }
      if (!attrs) {
        throw new Error("AI analysis is taking unusually long — please try again with a different photo.");
      }

      // 3) Apply the result (same as before).
      const a = attrs;
      setCommon((c) => ({
        ...c,
        articleType: a.article_type || c.articleType,
        hsn: c.hsn || a.hsn_code || "",
        fabric: a.fabric || c.fabric,
        sleeveLength: a.sleeve_length || c.sleeveLength,
        neck: a.neck || c.neck,
        pattern: a.pattern || c.pattern,
        type: a.type || c.type,
        fit: a.fit || c.fit,
        waistRise: a.waist_rise || c.waistRise,
        packSize: a.pack_size || c.packSize,
        packageContains: a.package_contains || c.packageContains,
        occasion: a.occasion || c.occasion,
        usage: a.usage || c.usage,
        washCare: a.wash_care || c.washCare,
        shape: a.shape || c.shape,
        hemline: a.hemline || c.hemline,
        materialCareDescription:
          a.material_care_description || c.materialCareDescription,
        sizeAndFitDescription:
          a.size_and_fit_description || c.sizeAndFitDescription,
        // BPC: AI reads the ingredient list from the packaging photo —
        // never overwrite what the seller already typed.
        ingredients: c.ingredients || a.ingredients || "",
        // BPC: volume/weight read from the label photo (e.g. "100ml") —
        // seller-typed values always win.
        quantityInMl: c.quantityInMl || a.net_quantity_ml || "",
        quantityInGram: c.quantityInGram || a.net_quantity_gm || "",
      }));
      setActiveSku((s) => ({
        ...s,
        vendorArticleName: a.article_name || s.vendorArticleName,
        productDisplayName: a.product_display_name || s.productDisplayName,
        listViewName: a.list_view_name || s.listViewName,
        prominentColour: a.prominent_colour || s.prominentColour,
        secondColour: a.is_pack ? "" : (a.second_colour || s.secondColour),
        thirdColour: a.is_pack ? "" : (a.third_colour || s.thirdColour),
        productDetails: a.product_details || s.productDetails,
        styleNote: a.style_note || s.styleNote,
        // Set/Co-ord component fields (Kurta Sets, Co-Ords…)
        topType: a.top_type || s.topType,
        bottomType: a.bottom_type || s.bottomType,
        dupatta: a.dupatta || s.dupatta,
        topPattern: a.top_pattern || s.topPattern,
        topLength: a.top_length || s.topLength,
      }));
      if (a.is_pack) {
        setIsPack(true);
        toast.message("Pack/combo detected — colour set to Multi");
      } else {
        // Reset — a stale true here forced "Multi" colour on every
        // subsequent single product (user bug report).
        setIsPack(false);
      }
      if (a.tags) setSeoTags(a.tags);
      const filled = Object.keys(a).length;
      setAiCount(filled);
      setAiDone(true);
      toast.success(`AI filled ${filled} attribute${filled === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(errMsg(e, "AI analysis failed"));
    } finally {
      clearInterval(elapsedTimer);
      setAiElapsed(0);
      setAiBusy(false);
    }
  }

  async function saveAsTemplate() {
    const name = window.prompt(
      "Name this listing template (e.g. 'Reebok men shirts')"
    );
    if (!name) return;
    try {
      const { data } = await api.post("/seller/listing-templates", {
        name,
        common,
        sku_blueprint: activeSku,
        size_mode: sizeMode,
        sizes,
        size_measures: sizeMeasures,
        is_pack: isPack,
        tags: seoTags,
      });
      toast.success(`Saved template "${data.name}"`);
    } catch (e) {
      toast.error(errMsg(e, "Couldn't save template"));
    }
  }

  async function loadTemplates() {
    try {
      const { data } = await api.get("/seller/listing-templates");
      return data;
    } catch {
      return [];
    }
  }

  function applyTemplate(t) {
    setCommon((c) => ({ ...c, ...t.common }));
    setActiveSku((s) => ({
      ...s,
      ...(t.sku_blueprint || {}),
      _rowId: s._rowId, // keep current row id
      images: s.images, // never overwrite uploaded photos
    }));
    setSizeMode(t.size_mode || "letters");
    setSizes(t.sizes || []);
    setSizeMeasures(t.size_measures || {});
    setIsPack(!!t.is_pack);
    setSeoTags(t.tags || "");
    setAiDone(false);
    toast.success(`Loaded template "${t.name}"`);
  }

  /** Apply attributes returned from the voice-fill endpoint.
   *  Voice schema:
   *  { productDisplayName, category, colour, fabric, fit, pattern,
   *    sleeve_length, neck, occasion, usage, wash_care, mrp,
   *    selling_price, sizes[], product_details, tags }
   *  Only OVERWRITES fields the voice actually mentioned (non-null/non-empty).
   *  Everything else is preserved — supports edit commands like
   *  "title ko Red Cotton Kurti kar do" without resetting other fields. */
  function applyVoiceAttrs(a, transcript) {
    if (!a || typeof a !== "object") return;
    const has = (v) => v !== null && v !== undefined && v !== "";
    const changed = [];

    setCommon((c) => {
      const next = { ...c };
      if (has(a.category)) { next.articleType = a.category; changed.push("category"); }
      if (has(a.fabric))   { next.fabric = a.fabric; changed.push("fabric"); }
      if (has(a.fit))      { next.fit = a.fit; changed.push("fit"); }
      if (has(a.pattern))  { next.pattern = a.pattern; changed.push("pattern"); }
      if (has(a.sleeve_length)) { next.sleeveLength = a.sleeve_length; changed.push("sleeve"); }
      if (has(a.neck))     { next.neck = a.neck; changed.push("neck"); }
      if (has(a.occasion)) { next.occasion = a.occasion; changed.push("occasion"); }
      if (has(a.usage))    { next.usage = a.usage; changed.push("usage"); }
      if (has(a.wash_care)){ next.washCare = a.wash_care; changed.push("wash care"); }
      return next;
    });
    setActiveSku((s) => {
      const next = { ...s };
      if (has(a.productDisplayName)) { next.productDisplayName = a.productDisplayName; changed.push("title"); }
      if (has(a.colour))             { next.prominentColour = a.colour; changed.push("colour"); }
      if (has(a.product_details))    { next.productDetails = a.product_details; changed.push("description"); }
      if (has(a.mrp))                { next.mrp = a.mrp; changed.push("MRP"); }
      if (has(a.selling_price))      { next.sellingPrice = a.selling_price; changed.push("price"); }
      return next;
    });
    if (has(a.tags)) { setSeoTags(a.tags); changed.push("tags"); }
    if (Array.isArray(a.sizes) && a.sizes.length) {
      setSizes(a.sizes.map(String));
      changed.push("sizes");
    }

    if (changed.length === 0) {
      // Nothing matched — surface the transcript so seller can rephrase.
      toast.error(
        transcript
          ? `Heard "${String(transcript).slice(0, 80)}" but couldn't map to any field. Try: "title change kro to Red Cotton Kurti".`
          : "Voice not understood — please try again."
      );
      return;
    }

    setAiDone(true);
    setAiCount(changed.length);
    toast.success(`Updated: ${changed.join(", ")} ✓`);
    if (transcript) {
      // Quieter secondary toast so seller can verify what Whisper heard.
      toast.message(`Heard: "${String(transcript).slice(0, 110)}${transcript.length > 110 ? "…" : ""}"`);
    }
  }

  /** Merge an Excel re-import payload into the page state.
   *  data = { category, skus[], common_data?, warnings[], fields_recovered } */
  function applyReimport(data) {
    if (!data?.skus?.length) return;
    setCommon((c) => ({
      ...c,
      articleType: data.category || c.articleType,
    }));
    // Mirror the Shopify ingest shape so the queue rendering code stays
    // identical. Each recovered SKU becomes its own pendingProduct row.
    const snapshots = data.skus.map((sku) => ({
      _id: crypto.randomUUID(),
      activeSku: {
        _rowId: crypto.randomUUID(),
        vendorSkuCode: sku.vendorSkuCode || "",
        vendorArticleName: sku.productDisplayName || "",
        productDisplayName: sku.productDisplayName || "",
        brandSize: sku.sizeIfExists || "",
        standardSize: sku.sizeIfExists || "",
        prominentColour: sku.colour || "",
        mrp: sku.mrp || "",
        isp: sku.sellingPrice || sku.mrp || "",
        productDetails: sku.productDetails || "",
        styleNote: sku.styleNote || "",
        images: {},
      },
      sizes: sku.sizeIfExists ? [sku.sizeIfExists] : ["Free Size"],
      sizeMeasures: {},
      sizeMode: "letter",
      isPack: false,
      seoTags: sku.tags || "",
      perProductCommon: {
        articleType: data.category || common.articleType || "Kurtis",
        brand: sku.brand || common.brand || "",
        fabric: sku.fabric || "",
        fit: sku.fit || "",
        pattern: sku.pattern || "",
        sleeveLength: sku.sleeveLength || "",
        neck: sku.neck || "",
        occasion: sku.occasion || "",
        usage: sku.usage || "",
        washCare: sku.washCare || "",
        hsn: sku.hsnCode || "",
      },
    }));
    setPendingProducts((prev) => [...prev, ...snapshots]);
    setAiDone(true);
  }


  async function saveDefaults() {
    const keys = [
      "manufacturer",
      "packer",
      "importer",
      "countryOfOrigin",
      "hsn",
      "ageGroup",
      "fashionType",
    ];
    const body = {};
    keys.forEach((k) => {
      if (common[k]) body[k] = common[k];
    });
    if (!Object.keys(body).length) {
      toast.error("Fill at least Brand to save defaults");
      return;
    }
    try {
      await api.put("/seller/compliance-defaults", body);
      toast.success("Defaults saved — auto-fills next time");
    } catch (e) {
      toast.error(errMsg(e, "Save failed"));
    }
  }

  function toggleSize(s) {
    setSizes((cur) =>
      cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]
    );
    // Footwear: pre-fill US / EURO / foot-length from the Myntra chart the
    // moment a UK size is picked (seller can still edit in the drawer).
    if (sizeMode === "shoe") {
      setSizeMeasures((cur) => (cur[s] ? cur : { ...cur, [s]: shoeDefaults(s) }));
    }
  }

  function validateBeforeGen() {
    if (!step1Done) return "Upload at least the Front Image (step 1)";
    if (!step3Done) return "Fill Brand + MRP (step 2)";
    if (!step4Done) return "Pick at least one size (step 4)";
    if (!common.manufacturer) {
      return "Manufacturer address missing — open Defaults and save it once";
    }
    if (!common.packer) {
      return "Packer address missing — open Defaults and save it once";
    }
    if (!common.countryOfOrigin) return "Country of Origin missing in Defaults";
    if (!common.hsn) return "HSN code missing in Defaults";
    if (!common.ageGroup) return "Age Group missing in Defaults";
    return null;
  }

  // Marketplace toggle — Myntra (default) or Meesho Excel output.
  const [marketplace, setMarketplace] = useState("myntra");
  const [meeshoMeta, setMeeshoMeta] = useState(null);
  const [meesho, setMeesho] = useState({
    category: "Tshirts", gst: "5", hsn: "6109", netWeight: "200", inventory: "100",
  });
  useEffect(() => {
    if (marketplace !== "meesho" || meeshoMeta) return;
    api.get("/seller/auto-listing/meesho-meta")
      .then(({ data }) => {
        setMeeshoMeta(data);
        if (data.categories?.length && !data.categories.includes(meesho.category)) {
          setMeesho((s) => ({ ...s, category: data.categories[0] }));
        }
      })
      .catch(() => toast.error("Could not load Meesho template info"));
    // eslint-disable-next-line
  }, [marketplace]);

  // ── Draft persistence: the AI Listing tab must survive a refresh AND
  // accidental clicks to other tabs (Upload Data, Myntra Hub…). Everything
  // the seller typed/uploaded is restored on return. Cleared after a
  // successful Excel generate.
  const DRAFT_KEY = "ecai_listing_draft_v1";
  const draftHydrated = useRef(false);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.activeSku) setActiveSku((s) => ({ ...s, ...d.activeSku }));
        if (Array.isArray(d.sizes) && d.sizes.length) setSizes(d.sizes);
        if (d.sizeMeasures) setSizeMeasures(d.sizeMeasures);
        if (d.sizeMode) setSizeMode(d.sizeMode);
        if (typeof d.productHint === "string") setProductHint(d.productHint);
        if (Array.isArray(d.pendingProducts) && d.pendingProducts.length) setPendingProducts(d.pendingProducts);
        if (typeof d.seoTags === "string") setSeoTags(d.seoTags);
        if (typeof d.isPack === "boolean") setIsPack(d.isPack);
        if (d.marketplace) setMarketplace(d.marketplace);
        if (d.meesho) setMeesho((s) => ({ ...s, ...d.meesho }));
        if (d.aiDone) setAiDone(true);
        if (d.aiCount) setAiCount(d.aiCount);
        if (d.commonOverrides) setCommon((c) => ({ ...c, ...d.commonOverrides }));
      }
    } catch { /* corrupt draft — start fresh */ }
    draftHydrated.current = true;
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!draftHydrated.current) return;
    const t = setTimeout(() => {
      try {
        // Blob preview URLs die on refresh — strip them; tiles fall back to
        // the backend image URL automatically.
        const strip = (sku) => ({
          ...sku,
          images: Object.fromEntries(
            Object.entries(sku?.images || {}).map(([k, v]) => [k, { ...v, local_url: undefined }])
          ),
        });
        const overrides = {};
        if (common.brand) overrides.brand = common.brand;
        if (common.articleType) overrides.articleType = common.articleType;
        localStorage.setItem(DRAFT_KEY, JSON.stringify({
          activeSku: strip(activeSku),
          sizes, sizeMeasures, sizeMode, productHint, seoTags, isPack,
          marketplace, meesho, aiDone, aiCount,
          pendingProducts: pendingProducts.map((p) =>
            p.activeSku ? { ...p, activeSku: strip(p.activeSku) } : p
          ),
          commonOverrides: overrides,
        }));
      } catch { /* storage full — non-fatal */ }
    }, 400);
    return () => clearTimeout(t);
  }, [activeSku, sizes, sizeMeasures, sizeMode, productHint, seoTags, isPack,
      marketplace, meesho, aiDone, aiCount, pendingProducts, common.brand, common.articleType]);

  async function generate() {
    // If there are no queued products, current must pass full validation.
    // Otherwise we silently accept an incomplete current (the user may have
    // just queued the last product and is ready to ship).
    const hasCurrent = !!Object.keys(activeSku.images || {}).length;
    if (pendingProducts.length === 0 || hasCurrent) {
      const err = validateBeforeGen();
      if (err) return toast.error(err);
    }

    setGenerating(true);
    setResult(null);
    setGenStatus("Submitting job…");
    setPendingJobId(null);

    // Helper — flattens one product snapshot into N per-size SKU rows.
    const flatten = (prod) => {
      // Fallback chain guarantees a non-empty name even when the seller
      // skipped AI Auto-Fill and typed nothing (fixes the recurring
      // "SKU #1 is missing vendorSkuCode or productDisplayName" error).
      const catName = prod.perProductCommon?.articleType || common.articleType || "";
      const baseName =
        prod.activeSku.vendorArticleName ||
        prod.activeSku.productDisplayName ||
        [common.brand, prod.perProductCommon?.prominentColour, catName]
          .filter(Boolean).join(" ") ||
        "Product";
      // Seller-typed SKU code wins; falls back to a slug of the name.
      // Backend mirrors vendorSkuCode → vendorArticleNumber, so the Excel
      // shows "CODE-SIZE" in BOTH columns (user spec, 2026-07).
      const customCode = (prod.activeSku.customSkuCode || "").trim();
      const baseSku =
        customCode || slug(baseName) || `SKU${Date.now().toString().slice(-6)}`;
      const displayName = prod.activeSku.productDisplayName || baseName;
      const groupId = slug(baseName);
      return prod.sizes.map((sz) => {
        // User spec (2026-08): Brand Size and Standard Size must be the SAME
        // value the seller picked in the software (no letter remapping).
        const stdSize = sz;
        const measures = prod.sizeMeasures[sz] || {};
        // Footwear rows: UK Size = picked size; US / EURO / foot length
        // default from the Myntra chart, overridden by drawer edits.
        const shoeFields =
          prod.sizeMode === "shoe" ? { ukSize: sz, ...shoeDefaults(sz) } : {};
        // Public no-login image URLs (object-storage backed) — required by
        // the Meesho Excel; harmless extra key for the Myntra generator.
        const imgMap = prod.activeSku?.images || prod.images || {};
        const imageUrls = IMAGE_COLS
          .map((k) => imgMap[k])
          .filter((im) => im && (im.url || im.relative_url))
          .map((im) => im.url || `${BACKEND_URL}${im.relative_url}`);
        return {
          vendorSkuCode: `${baseSku}-${sz}`,
          vendorArticleName: prod.activeSku.vendorArticleName || baseName,
          productDisplayName: displayName,
          listViewName: prod.activeSku.listViewName,
          styleGroupId: groupId,
          brandSize: sz,
          standardSize: stdSize,
          prominentColour: prod.isPack ? "Multi" : prod.activeSku.prominentColour,
          secondColour: prod.isPack ? "" : prod.activeSku.secondColour,
          thirdColour: prod.isPack ? "" : prod.activeSku.thirdColour,
          mrp: prod.activeSku.mrp,
          isp: prod.activeSku.isp,
          productDetails: prod.activeSku.productDetails,
          styleNote: prod.activeSku.styleNote,
          pattern: prod.perProductCommon?.pattern || "",
          topType: prod.activeSku.topType,
          bottomType: prod.activeSku.bottomType,
          dupatta: prod.activeSku.dupatta,
          topPattern: prod.activeSku.topPattern,
          topLength: prod.activeSku.topLength,
          images: imageUrls,
          ...shoeFields,
          ...measures,
        };
      });
    };

    // Build the combined list of products in queue order; the active
    // product (if filled) goes last.
    const allProducts = [...pendingProducts];
    if (hasCurrent) {
      allProducts.push({
        _id: "current",
        activeSku,
        sizes,
        sizeMeasures,
        sizeMode,
        isPack,
        seoTags,
        perProductCommon: {
          articleType: common.articleType,
          fabric: common.fabric,
          sleeveLength: common.sleeveLength,
          neck: common.neck,
          pattern: common.pattern,
          occasion: common.occasion,
          washCare: common.washCare,
          shape: common.shape,
          hemline: common.hemline,
          materialCareDescription: common.materialCareDescription,
          sizeAndFitDescription: common.sizeAndFitDescription,
        },
      });
    }

    if (!allProducts.length) {
      setGenerating(false);
      return toast.error("Nothing to generate — upload at least one product");
    }

    // All queued products must share the SAME category (one Myntra template
    // = one category). Reject mismatches with a clear error.
    const cats = new Set(
      allProducts.map((p) => p.perProductCommon?.articleType || common.articleType)
    );
    if (cats.size > 1) {
      setGenerating(false);
      return toast.error(
        `All queued products must share one category. Found: ${[...cats].join(", ")}`
      );
    }
    const category = [...cats][0];
    if (!category) {
      setGenerating(false);
      return toast.error("Pick a category first (Step 2)");
    }

    // Flatten every product → one big SKU array.
    const allSkus = allProducts.flatMap(flatten);

    // Merge seoTags from all products (dedup, comma-joined).
    const mergedTags = Array.from(
      new Set(
        allProducts
          .flatMap((p) => (p.seoTags || "").split(/[,\n]/g))
          .map((s) => s.trim())
          .filter(Boolean)
      )
    ).join(", ");

    try {
      const payload = {
        category: marketplace === "meesho" ? meesho.category : category,
        marketplace,
        common: {
          ...common,
          tags: mergedTags || seoTags,
          isPack: false,
          ...(marketplace === "meesho"
            ? {
                gstPercent: meesho.gst,
                hsnId: meesho.hsn,
                netWeightGms: meesho.netWeight,
                inventory: meesho.inventory,
              }
            : {}),
        },
        skus: allSkus,
      };
      // Step 1 — kick off the background job (may return inline-done).
      // Generous 90 s POST timeout: a heavily-loaded pod can take a few
      // seconds to deduct credits + insert the job doc + start the
      // worker, especially on the first call after a cold restart. The
      // actual Excel build runs in the background and is observed via
      // long-poll, so this timeout only protects against a truly dead
      // ingress (Cloudflare 504).
      const { data: kickoff } = await api.post(
        "/seller/auto-listing/generate",
        payload,
        { timeout: 90000 }
      );
      if (!kickoff?.job_id) {
        throw new Error("Server did not return a job id");
      }

      // Step 2 — long-poll. `?wait=5` holds the request server-side
      // until the Excel build finishes or 5s elapse → far fewer
      // round-trips than the old 500ms client loop.
      const startedAt = Date.now();
      // Reduced from 120s → 45s: with the 2026-07 permanent-fix
      // (18s inline hold + 8s auto-rescue + 30s server-side fail),
      // any legitimate build lands in <35s. Anything past 45s is
      // definitely stuck — surface the retry option immediately.
      const TIMEOUT_MS = 45000;
      let job = null;
      // Preserve the payload so a one-click Retry uses the exact same
      // inputs (no re-typing SKUs, no lost work).
      setLastGenPayload(payload);
      if (kickoff.status === "done") {
        job = kickoff;
      } else if (kickoff.status === "failed") {
        throw new Error(kickoff.error || "Excel generation failed");
      } else {
        // Switch the user-facing label as soon as we know it's a real
        // background build — "Submitting job…" is confusing once the
        // job is already submitted and being processed.
        setGenStatus("Building your Excel…");
        while (Date.now() - startedAt < TIMEOUT_MS) {
          const { data: status } = await api.get(
            `/seller/auto-listing/generate/job/${kickoff.job_id}?wait=5`,
            { timeout: 10000 }
          );
          if (status.status === "done") { job = status; break; }
          if (status.status === "failed") {
            // On explicit failure, credits are auto-refunded by the
            // backend. Preserve the failed job id so Retry can reuse it.
            setPendingJobId(kickoff.job_id);
            refreshCreditInfo();
            throw new Error(status.error || "Excel generation failed");
          }
          // Update on EVERY poll cycle so the seller knows it's alive.
          const elapsed = Math.round((Date.now() - startedAt) / 1000);
          setGenStatus(`Building your Excel… ${elapsed}s`);
        }
      }
      if (!job) {
        // Worker is still busy — don't lose the job_id. Surface it so the
        // seller can resume later instead of starting over.
        setPendingJobId(kickoff.job_id);
        throw new Error(
          "Excel is taking longer than expected. Click 'Retry' to try again with the same inputs — no credits will be double-charged."
        );
      }

      setResult({
        job_id: kickoff.job_id,
        filename: job.filename,
        download_url: job.download_url,
        category: job.category,
        rows: job.rows,
        size_kb: job.size_kb,
        products: allProducts.length,
      });
      // Success — clear retry state so we don't accidentally re-submit.
      setPendingJobId(null);
      setLastGenPayload(null);
      // Draft served its purpose — next visit starts a fresh product.
      try { localStorage.removeItem("ecai_listing_draft_v1"); } catch { /* noop */ }
      toast.success(
        `Excel ready — ${allProducts.length} product(s) · ${job.rows} row(s)`
      );
      refreshCreditInfo();
      // Demo/credits khatam? Show the self-serve recharge immediately so
      // the seller can pay & keep listing without waiting for admin.
      try {
        const { data: me } = await api.get("/auth/me");
        if (Number(me.listings_credits || 0) <= 0) setShowRecharge(true);
      } catch { /* non-critical */ }
    } catch (e) {
      toast.error(errMsg(e, "Generation failed"));
    } finally {
      setGenerating(false);
      setGenStatus("");
    }
  }

  // Resume polling for a job that timed out client-side. Useful when the
  // worker is busy (cold cache / large batch) — the seller doesn't have to
  // regenerate from scratch, we just re-check the existing job.
  async function resumePendingJob() {
    if (!pendingJobId) return;
    setGenerating(true);
    setGenStatus("Re-checking job status…");
    try {
      const startedAt = Date.now();
      const TIMEOUT_MS = 120000;
      let job = null;
      while (Date.now() - startedAt < TIMEOUT_MS) {
        const { data: status } = await api.get(
          `/seller/auto-listing/generate/job/${pendingJobId}?wait=5`,
          { timeout: 10000 }
        );
        if (status.status === "done") { job = status; break; }
        if (status.status === "failed") throw new Error(status.error || "Excel generation failed");
        const elapsed = Math.round((Date.now() - startedAt) / 1000);
        setGenStatus(`Building your Excel… ${elapsed}s`);
      }
      if (!job) throw new Error("Still building. Please try Resume again in a few seconds.");
      setResult({
        job_id: pendingJobId,
        filename: job.filename,
        download_url: job.download_url,
        category: job.category,
        rows: job.rows,
        size_kb: job.size_kb,
      });
      setPendingJobId(null);
      try { localStorage.removeItem("ecai_listing_draft_v1"); } catch { /* noop */ }
      toast.success(`Excel ready — ${job.rows} row(s)`);
    } catch (e) {
      toast.error(errMsg(e, "Resume failed"));
    } finally {
      setGenerating(false);
      setGenStatus("");
    }
  }

  const token = getToken();
  const downloadUrl = result
    ? `${BACKEND_URL}${result.download_url}?token=${token}`
    : null;

  if (loading) {
    return (
      <AppShell role="seller">
        <div className="flex items-center gap-2 text-white/60">
          <Loader2 size={16} className="animate-spin" /> Loading templates…
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell role="seller">
      <div className="max-w-3xl mx-auto">
        {/* header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#4F46E5]/10 border border-[#4F46E5]/30 grid place-items-center">
              <FileSpreadsheet size={18} className="text-[#4F46E5]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Auto Listing</h1>
              <div className="text-xs text-white/50 mt-0.5">
                5 steps. ~2 minutes. Myntra-ready Excel.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {creditInfo && <CreditUsageBadge info={creditInfo} />}
            <TemplatesButton
              loadTemplates={loadTemplates}
              applyTemplate={applyTemplate}
            />
            <button
              onClick={() => setShowDefaults(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/10 text-white/80 text-xs font-semibold"
              data-testid="open-defaults-btn"
            >
              <Settings2 size={12} /> Defaults
            </button>
            <Link
              to="/seller/auto-listing/advanced"
              className="text-[11px] text-white/50 hover:text-white px-2 py-1.5"
            >
              Advanced mode →
            </Link>
          </div>
        </div>

        <div className="space-y-3">
          {/* BULK QUEUE — shows products already saved for one combined Excel */}
          {pendingProducts.length > 0 && (
            <div
              className="rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.06] p-4"
              data-testid="bulk-queue-banner"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Package size={14} className="text-emerald-300" />
                  <div className="text-sm font-bold text-emerald-100">
                    Bulk queue · {pendingProducts.length} product
                    {pendingProducts.length === 1 ? "" : "s"} saved
                  </div>
                </div>
                <div className="text-[11px] text-white/60">
                  Generate will produce <b>one Excel</b> with all queued products
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {pendingProducts.map((p, i) => {
                  const label =
                    p.activeSku.vendorArticleName ||
                    p.activeSku.productDisplayName ||
                    `Product ${i + 1}`;
                  const sizeCount = p.sizes.length;
                  return (
                    <div
                      key={p._id}
                      className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-black/30 border border-emerald-400/30 text-[11px]"
                      data-testid={`bulk-queue-item-${i}`}
                    >
                      <span className="font-semibold text-white truncate max-w-[140px]">
                        {label}
                      </span>
                      <span className="text-white/50">
                        · {sizeCount} size{sizeCount === 1 ? "" : "s"}
                      </span>
                      <button
                        onClick={() => removeFromQueue(p._id)}
                        className="text-white/50 hover:text-red-400"
                        data-testid={`bulk-queue-remove-${i}`}
                        title="Remove from queue"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="text-[10px] text-white/50 mt-2 leading-relaxed">
                Tip: all queued products must share the same category — they
                map to one Myntra template. Compliance defaults (brand,
                manufacturer, packer, HSN) are inherited automatically.
              </div>
            </div>
          )}

          {/* STEP 1 — PHOTOS */}
          <StepCard
            step={1}
            title="Upload product photos"
            subtitle="Front view is required; side, back, look-shot help conversion."
            done={step1Done}
          >
            <PhotoUploadZone
              assets={activeSku.images || {}}
              onAdd={addImages}
              onRemove={removeImage}
              onSaveAndAddNew={commitCurrentToQueue}
              pendingCount={pendingProducts.length}
              onShopifyClick={() => setShopifyOpen(true)}
            />
          </StepCard>

          {/* STEP 2 — BRAND & PRICE */}
          <StepCard
            step={2}
            title="Brand & Price"
            subtitle="Everything else (manufacturer, HSN, country…) comes from your saved defaults."
            done={step3Done}
            locked={!step1Done}
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-white/55 mb-1">
                  Brand <span className="text-[#4F46E5]">*</span>
                </div>
                <input
                  value={common.brand || ""}
                  onChange={(e) =>
                    setCommon((c) => ({ ...c, brand: e.target.value }))
                  }
                  placeholder="e.g. Fabindia"
                  className="w-full px-3 py-2 bg-black/40 border border-white/15 rounded-md text-white text-sm focus:outline-none focus:border-[#4F46E5]"
                  data-testid="quick-brand"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-white/55 mb-1">
                  SKU Code{" "}
                  <span className="text-white/35 normal-case tracking-normal">
                    (optional)
                  </span>
                </div>
                <input
                  value={activeSku.customSkuCode || ""}
                  onChange={(e) =>
                    setActiveSku((s) => ({ ...s, customSkuCode: e.target.value }))
                  }
                  placeholder="e.g. FDF-NP-01"
                  className="w-full px-3 py-2 bg-black/40 border border-white/15 rounded-md text-white text-sm focus:outline-none focus:border-[#4F46E5]"
                  data-testid="quick-sku-code"
                />
                <div className="mt-1 text-[9px] text-white/35 leading-snug">
                  Goes to Excel as vendorSkuCode &amp; vendorArticleNumber
                  (code + size, e.g. FDF-NP-01-M)
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-white/55 mb-1">
                  MRP (₹) <span className="text-[#4F46E5]">*</span>
                </div>
                <input
                  type="number"
                  value={activeSku.mrp || ""}
                  onChange={(e) =>
                    setActiveSku((s) => ({ ...s, mrp: e.target.value }))
                  }
                  placeholder="1999"
                  className="w-full px-3 py-2 bg-black/40 border border-white/15 rounded-md text-white text-sm focus:outline-none focus:border-[#4F46E5]"
                  data-testid="quick-mrp"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-white/55 mb-1">
                  Selling Price (₹)
                </div>
                <input
                  type="number"
                  value={activeSku.isp || ""}
                  onChange={(e) =>
                    setActiveSku((s) => ({ ...s, isp: e.target.value }))
                  }
                  placeholder="1299"
                  className="w-full px-3 py-2 bg-black/40 border border-white/15 rounded-md text-white text-sm focus:outline-none focus:border-[#4F46E5]"
                  data-testid="quick-isp"
                />
              </div>
            </div>
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-white/55 mb-1">
                Category {step2Done && "(AI-detected)"}
              </div>
              <CategoryPicker
                value={common.articleType}
                groups={meta?.category_groups || {}}
                onChange={(v) =>
                  setCommon((c) => ({ ...c, articleType: v }))
                }
              />
            </div>
            {isBPCCategory && (
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-widest font-bold text-white/55 mb-1 flex items-center justify-between">
                  <span>Ingredients <span className="text-white/35 normal-case tracking-normal">(BPC only — comma-separated)</span></span>
                  <span className="text-white/35 text-[9px]">{(common.ingredients || "").length}/500</span>
                </div>
                <textarea
                  rows={2}
                  maxLength={500}
                  value={common.ingredients || ""}
                  onChange={(e) =>
                    setCommon((c) => ({ ...c, ingredients: e.target.value }))
                  }
                  placeholder="e.g. Aqua, Glycerin, Shea Butter, Vitamin E, Hyaluronic Acid"
                  className="w-full px-3 py-2 bg-black/40 border border-white/15 rounded-md text-white text-sm focus:outline-none focus:border-[#4F46E5]"
                  data-testid="quick-ingredients"
                />
              </div>
            )}
          </StepCard>

          {/* STEP 3 — AI */}
          <StepCard
            step={3}
            title="AI Auto-Fill"
            subtitle="Detects category, colour, fabric, neck, sleeve, occasion, pattern, product details & style note."
            done={step2Done}
            locked={!step1Done}
          >
            <div className="mb-3">
              <label className="block text-[10px] uppercase tracking-widest font-bold text-white/55 mb-1">
                Tell AI what this product is{" "}
                <span className="text-white/35 normal-case tracking-normal">
                  (optional but helps — e.g. "Anarkali Kurti", "Hipster Brief Pack of 3", "Block Heel")
                </span>
              </label>
              <input
                type="text"
                value={productHint}
                onChange={(e) => setProductHint(e.target.value)}
                placeholder="e.g. Cotton Kurti, Striped Tshirt, Anarkali Dress…"
                maxLength={120}
                className="w-full px-3 py-2 bg-black/40 border border-white/15 rounded-md text-white text-sm focus:outline-none focus:border-[#4F46E5]"
                data-testid="ai-product-hint"
              />
            </div>
            <button
              onClick={runAi}
              disabled={aiBusy}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-[#4F46E5] to-[#7C3AED] hover:from-[#4338CA] hover:to-[#6D28D9] disabled:opacity-50 text-sm font-bold shadow-lg shadow-indigo-500/25"
              style={{ color: "#FFFFFF" }}
              data-testid="ai-fill-btn"
            >
              {aiBusy ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Analyzing image
                  ({aiElapsed}s)
                  {aiElapsed > 60
                    ? " — Claude is slow today, hang tight…"
                    : aiElapsed > 30
                    ? " — almost done…"
                    : "…"}
                </>
              ) : (
                <>
                  <Sparkles size={14} /> Run AI Auto-Fill
                </>
              )}
            </button>
            {aiDone && (
              <div className="mt-3 space-y-3">
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div className="px-3 py-2 rounded-md bg-emerald-500/10 border border-emerald-500/30">
                    <div className="text-white/60">Detected category</div>
                    <div className="font-mono-data text-white font-semibold">
                      {common.articleType}
                    </div>
                  </div>
                  <div className="px-3 py-2 rounded-md bg-emerald-500/10 border border-emerald-500/30">
                    <div className="text-white/60">Attributes filled</div>
                    <div className="font-mono-data text-white font-semibold">
                      {aiCount}
                    </div>
                  </div>
                </div>
                {/* SEO preview / editable */}
                <div className="p-3 rounded-lg bg-black/30 border border-white/10 space-y-3">
                  <div>
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-bold text-white/55 mb-1">
                      <span>Title (SEO)</span>
                      <span
                        className={
                          (activeSku.productDisplayName || "").length > 90
                            ? "text-[#4F46E5]"
                            : "text-white/40"
                        }
                      >
                        {(activeSku.productDisplayName || "").length} / 90
                      </span>
                    </div>
                    <input
                      maxLength={90}
                      value={activeSku.productDisplayName || ""}
                      onChange={(e) =>
                        setActiveSku((s) => ({
                          ...s,
                          productDisplayName: e.target.value,
                        }))
                      }
                      data-testid="seo-title"
                      className="w-full px-3 py-2 bg-black/40 border border-white/15 rounded-md text-white text-sm focus:outline-none focus:border-[#4F46E5]"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-bold text-white/55 mb-1">
                      <span>Product description (SEO)</span>
                      <span
                        className={
                          (activeSku.productDetails || "").length > 200
                            ? "text-[#4F46E5]"
                            : "text-white/40"
                        }
                      >
                        {(activeSku.productDetails || "").length} / 200
                      </span>
                    </div>
                    <textarea
                      rows={2}
                      maxLength={200}
                      value={activeSku.productDetails || ""}
                      onChange={(e) =>
                        setActiveSku((s) => ({
                          ...s,
                          productDetails: e.target.value,
                        }))
                      }
                      data-testid="seo-description"
                      className="w-full px-3 py-2 bg-black/40 border border-white/15 rounded-md text-white text-sm focus:outline-none focus:border-[#4F46E5]"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-white/55 mb-1">
                      SEO tags / keywords
                    </div>
                    <input
                      value={seoTags}
                      onChange={(e) => setSeoTags(e.target.value)}
                      placeholder="cotton kurti, festive wear, indian ethnic, …"
                      data-testid="seo-tags"
                      className="w-full px-3 py-2 bg-black/40 border border-white/15 rounded-md text-white text-sm focus:outline-none focus:border-[#4F46E5]"
                    />
                  </div>
                </div>
              </div>
            )}
          </StepCard>

          {/* STEP 4 — SIZES (volume buckets for BPC, garment sizes otherwise) */}
          <StepCard
            step={4}
            title={
              isBPCCategory
                ? "Pick volume / pack size"
                : isShoeCategory
                ? "Pick UK shoe sizes"
                : "Pick sizes"
            }
            subtitle={
              isBPCCategory
                ? "Beauty products ship in ML/gm volumes. Pick the bucket(s) you sell."
                : isShoeCategory
                ? "Each UK size becomes its own SKU row. US / EURO / foot length auto-fill from the Myntra size chart."
                : "Each size becomes its own SKU row in the Excel."
            }
            done={step4Done}
            locked={!step3Done}
          >
            {!isBPCCategory && !isShoeCategory && (
              <div className="flex items-center gap-2 mb-3">
                <button
                  onClick={() => { setSizeMode("letters"); setSizes([]); }}
                  className={`px-3 py-1 rounded-md text-[11px] font-bold ${
                    sizeMode === "letters"
                      ? "bg-[#4F46E5] text-white"
                      : "bg-white/10 text-white/70 hover:bg-white/15"
                  }`}
                  data-testid="size-mode-letters"
                >
                  S / M / L / XL
                </button>
                <button
                  onClick={() => { setSizeMode("shirt"); setSizes([]); }}
                  className={`px-3 py-1 rounded-md text-[11px] font-bold ${
                    sizeMode === "shirt"
                      ? "bg-[#4F46E5] text-white"
                      : "bg-white/10 text-white/70 hover:bg-white/15"
                  }`}
                  data-testid="size-mode-shirt"
                >
                  32 / 34 / 36 / 38 (shirt)
                </button>
                <div className="text-[10px] text-white/40 ml-auto">
                  {sizeMode === "shirt"
                    ? "Numeric sizes map to S–4XL on Myntra"
                    : "Letter sizes"}
                </div>
              </div>
            )}
            {isBPCCategory && (
              <div className="mb-3 space-y-3">
                <div className="text-[10px] text-white/40">
                  Volume / weight buckets — pick one or more to ship as separate SKUs.
                </div>
                <div className="flex flex-wrap items-end gap-3 rounded-lg border border-white/10 bg-white/5 p-3">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wide text-white/50 mb-1">
                      Qty in gm
                    </div>
                    <input
                      type="number"
                      min="0"
                      placeholder="e.g. 50"
                      value={common.quantityInGram || ""}
                      onChange={(e) =>
                        setCommon((c) => ({ ...c, quantityInGram: e.target.value }))
                      }
                      className="w-24 rounded-md bg-black/30 border border-white/15 px-2 py-1.5 text-xs text-white placeholder:text-white/25"
                      data-testid="bpc-qty-gm"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wide text-white/50 mb-1">
                      Qty in ml
                    </div>
                    <input
                      type="number"
                      min="0"
                      placeholder="e.g. 100"
                      value={common.quantityInMl || ""}
                      onChange={(e) =>
                        setCommon((c) => ({ ...c, quantityInMl: e.target.value }))
                      }
                      className="w-24 rounded-md bg-black/30 border border-white/15 px-2 py-1.5 text-xs text-white placeholder:text-white/25"
                      data-testid="bpc-qty-ml"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wide text-white/50 mb-1">
                      Qty in pieces
                    </div>
                    <input
                      type="number"
                      min="1"
                      placeholder={`auto (${common.packSize || 1})`}
                      value={common.quantityInPieces || ""}
                      onChange={(e) =>
                        setCommon((c) => ({ ...c, quantityInPieces: e.target.value }))
                      }
                      className="w-24 rounded-md bg-black/30 border border-white/15 px-2 py-1.5 text-xs text-white placeholder:text-white/25"
                      data-testid="bpc-qty-pc"
                    />
                  </div>
                  <div className="text-[10px] text-white/45 max-w-[240px] leading-relaxed">
                    These exact values fill the Excel's "Quantity in gram / ml /
                    pieces" columns. Leave Pieces blank — it auto-fills from the
                    pack count (1 product = 1, pack of 2 = 2).
                  </div>
                </div>
              </div>
            )}
            {isShoeCategory && (
              <div className="text-[10px] text-white/40 mb-3" data-testid="shoe-size-hint">
                UK shoe sizes — US Size, EURO Size &amp; To Fit Foot Length (cm)
                auto-fill from the Myntra chart (editable per size).
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {currentSizeList.map((s) => (
                <button
                  key={s}
                  onClick={() => toggleSize(s)}
                  data-testid={`size-${s}`}
                  className={`px-4 py-1.5 rounded-md text-xs font-semibold border ${
                    sizes.includes(s)
                      ? "bg-[#4F46E5]/15 border-[#4F46E5]/60 text-white"
                      : "bg-white/5 border-white/15 text-white/70 hover:text-white"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            {sizes.length > 0 ? (
              <div className="mt-3 flex items-center justify-between gap-3 text-[11px]">
                <div className="text-white/60">
                  Will generate {sizes.length} SKU
                  {sizes.length === 1 ? "" : "s"} ({sizes.join(", ")})
                </div>
                {!isBPCCategory && (
                  <button
                    onClick={() => setShowMeasures(true)}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md font-semibold border ${
                      Object.keys(sizeMeasures).length
                        ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-100 hover:bg-emerald-500/25"
                        : "bg-[#4F46E5]/15 border-[#4F46E5]/50 text-white hover:bg-[#4F46E5]/25"
                    }`}
                    data-testid="open-measures-btn"
                  >
                    <Ruler size={12} />
                    {Object.keys(sizeMeasures).length
                      ? `${Object.keys(sizeMeasures).length} size${Object.keys(sizeMeasures).length === 1 ? "" : "s"} measured · Edit`
                      : sizeMode === "shoe"
                      ? "View / edit size chart (US · EURO · foot length)"
                      : "Add measurements (chest, waist, length…)"}
                  </button>
                )}
              </div>
            ) : (
              <div className="mt-3 text-[11px] text-white/50 flex items-center gap-1.5">
                {isBPCCategory ? (
                  <>Pick at least one volume / pack size above.</>
                ) : isShoeCategory ? (
                  <>
                    <Ruler size={11} />
                    Pick at least one UK size — US / EURO / foot length will
                    auto-fill the Myntra Excel columns.
                  </>
                ) : (
                  <>
                    <Ruler size={11} />
                    Pick at least one size to add chest / waist / length measurements
                    — they'll auto-fill the Myntra Excel columns.
                  </>
                )}
              </div>
            )}
            <label className="mt-3 flex items-center gap-2 text-[11px] text-white/70 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={isPack}
                onChange={(e) => setIsPack(e.target.checked)}
                data-testid="pack-toggle"
                className="accent-[#4F46E5]"
              />
              <Package size={12} />
              This product is a <b className="text-white">pack / combo</b>{" "}
              (sets Prominent Colour = "Multi")
            </label>
          </StepCard>

          {/* STEP 5 — GENERATE */}
          <StepCard
            step={5}
            title={marketplace === "meesho" ? "Generate Meesho Excel" : "Generate Myntra Excel"}
            subtitle={
              pendingProducts.length > 0
                ? `One-click download. Output combines ${pendingProducts.length} queued + current into one Excel.`
                : "One-click download. Output is trimmed to just your category sheet."
            }
            done={!!result}
            locked={!allDone && pendingProducts.length === 0}
          >
            {/* Marketplace toggle */}
            <div className="flex gap-2 mb-3" data-testid="marketplace-toggle">
              {[
                { id: "myntra", label: "Myntra Excel" },
                { id: "meesho", label: "Meesho Excel" },
              ].map((m) => (
                <button
                  key={m.id}
                  onClick={() => setMarketplace(m.id)}
                  className={`px-4 py-2 rounded-lg text-xs font-bold border transition-colors ${
                    marketplace === m.id
                      ? "bg-[#4F46E5] border-[#4F46E5]"
                      : "bg-white/5 border-white/15 text-white/70 hover:border-white/30"
                  }`}
                  style={marketplace === m.id ? { color: "#FFFFFF" } : undefined}
                  data-testid={`marketplace-${m.id}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            {marketplace === "meesho" && (
              <div className="mb-4 p-3 rounded-xl bg-white/[0.04] border border-white/10" data-testid="meesho-fields">
                <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-2">
                  Meesho details (compulsory columns)
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  <label className="text-[10px] text-white/60 font-semibold">
                    Category
                    <select
                      value={meesho.category}
                      onChange={(e) => setMeesho((s) => ({ ...s, category: e.target.value }))}
                      className="mt-1 w-full px-2 py-2 rounded-md bg-black/40 border border-white/15 text-white text-xs"
                      data-testid="meesho-category"
                    >
                      {(meeshoMeta?.categories || [meesho.category]).map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </label>
                  <label className="text-[10px] text-white/60 font-semibold">
                    GST %
                    <select
                      value={meesho.gst}
                      onChange={(e) => setMeesho((s) => ({ ...s, gst: e.target.value }))}
                      className="mt-1 w-full px-2 py-2 rounded-md bg-black/40 border border-white/15 text-white text-xs"
                      data-testid="meesho-gst"
                    >
                      {(meeshoMeta?.per_category?.[meesho.category]?.gst_options || ["5", "18"]).map((g) => (
                        <option key={g} value={g}>{g}%</option>
                      ))}
                    </select>
                  </label>
                  <label className="text-[10px] text-white/60 font-semibold">
                    HSN ID
                    <input
                      value={meesho.hsn}
                      onChange={(e) => setMeesho((s) => ({ ...s, hsn: e.target.value }))}
                      list="meesho-hsn-list"
                      className="mt-1 w-full px-2 py-2 rounded-md bg-black/40 border border-white/15 text-white text-xs"
                      data-testid="meesho-hsn"
                    />
                    <datalist id="meesho-hsn-list">
                      {(meeshoMeta?.per_category?.[meesho.category]?.hsn_options || []).map((h) => (
                        <option key={h} value={h} />
                      ))}
                    </datalist>
                  </label>
                  <label className="text-[10px] text-white/60 font-semibold">
                    Net Weight (gms)
                    <input
                      type="number"
                      value={meesho.netWeight}
                      onChange={(e) => setMeesho((s) => ({ ...s, netWeight: e.target.value }))}
                      className="mt-1 w-full px-2 py-2 rounded-md bg-black/40 border border-white/15 text-white text-xs"
                      data-testid="meesho-netweight"
                    />
                  </label>
                  <label className="text-[10px] text-white/60 font-semibold">
                    Inventory
                    <input
                      type="number"
                      value={meesho.inventory}
                      onChange={(e) => setMeesho((s) => ({ ...s, inventory: e.target.value }))}
                      className="mt-1 w-full px-2 py-2 rounded-md bg-black/40 border border-white/15 text-white text-xs"
                      data-testid="meesho-inventory"
                    />
                  </label>
                </div>
                <div className="text-[10px] text-white/40 mt-2">
                  Image links auto-fill from your uploaded photos — public URLs, no login needed.
                  Price/MRP, sizes, colour, fabric etc. flow from steps 1-4.
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={generate}
                disabled={generating}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-[#4F46E5] to-[#7C3AED] hover:from-[#4338CA] hover:to-[#6D28D9] disabled:opacity-50 text-sm font-bold shadow-lg shadow-indigo-500/25"
                style={{ color: "#FFFFFF" }}
                data-testid="quick-generate-btn"
              >
                {generating ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    {genStatus || "Building Excel…"}
                  </>
                ) : (
                  <>
                    <FileSpreadsheet size={14} />
                    {pendingProducts.length > 0
                      ? ` Generate Excel (${
                          pendingProducts.length + (Object.keys(activeSku.images || {}).length ? 1 : 0)
                        } products)`
                      : " Generate Excel"}
                  </>
                )}
              </button>
              <button
                onClick={saveAsTemplate}
                disabled={!step3Done}
                className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/15 disabled:opacity-40 text-white text-xs font-bold shrink-0"
                data-testid="save-as-template-btn"
                title="Save this configuration so you can spin up similar SKUs in 60s next time."
              >
                <BookmarkPlus size={14} /> Save as template
              </button>
            </div>
            {pendingJobId && !result && !generating && (
              <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 flex flex-wrap items-center justify-between gap-3" data-testid="pending-job-banner">
                <div>
                  <div className="text-sm font-semibold text-white flex items-center gap-2">
                    <AlertTriangle size={13} className="text-amber-300" />
                    Excel took longer than expected
                  </div>
                  <div className="text-[11px] text-white/60 mt-0.5">
                    Credits auto-refunded. Click <b className="text-amber-200">Retry</b> to
                    generate the same Excel again, or <b className="text-white">Resume</b> to
                    check if the server eventually finished.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {lastGenPayload && (
                    <button
                      onClick={() => {
                        setPendingJobId(null);
                        generate(lastGenPayload);
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gradient-to-r from-fuchsia-500 to-indigo-500 hover:from-fuchsia-400 hover:to-indigo-400 text-white text-xs font-bold shadow-[0_6px_18px_-6px_rgba(217,70,239,0.6)]"
                      data-testid="retry-gen-btn"
                    >
                      <RotateCcw size={12} /> Retry
                    </button>
                  )}
                  <button
                    onClick={resumePendingJob}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-400 text-black text-xs font-bold"
                    data-testid="resume-job-btn"
                  >
                    <Loader2 size={12} /> Resume
                  </button>
                </div>
              </div>
            )}
            {result && downloadUrl && (
              <div className="mt-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-white">
                    {result.filename}
                  </div>
                  <div className="text-[11px] text-white/60">
                    {result.products ? `${result.products} product(s) · ` : ""}
                    {result.rows} row(s) · {result.size_kb} KB · {result.category}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={downloadUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-bold"
                    data-testid="quick-download-btn"
                  >
                    <Download size={12} /> Download .xlsx
                  </a>
                  <PushToMyntraButton jobId={result.job_id || result.report_id} />
                </div>
              </div>
            )}
          </StepCard>
        </div>

        {/* DEFAULTS DRAWER */}
        {showDefaults && (
          <DefaultsDrawer
            common={common}
            setCommon={setCommon}
            meta={meta}
            onSave={async () => {
              await saveDefaults();
              setShowDefaults(false);
            }}
            onClose={() => setShowDefaults(false)}
          />
        )}

        {/* SELF-SERVE CREDITS RECHARGE (₹10 = 1 listing) */}
        <RechargeModal
          open={showRecharge}
          onClose={() => setShowRecharge(false)}
          onSuccess={() => refreshCreditInfo()}
        />

        {/* TUTORIAL / SUPPORT — left steps card, right-side cards + video popup */}
        <StepsSidebar />
        <TutorialSidebar
          onWatch={() => setShowTutorial(true)}
          onOpenDefaults={() => setShowDefaults(true)}
        />
        <TutorialVideoModal open={showTutorial} onClose={() => setShowTutorial(false)} />

        {/* MEASUREMENTS DRAWER */}
        {showMeasures && (
          <MeasurementsDrawer
            sizes={sizes}
            values={sizeMeasures}
            onChange={setSizeMeasures}
            onClose={() => setShowMeasures(false)}
            profile={
              sizeMode === "shoe"
                ? "shoe"
                : sizeMode === "shirt" || isChestMeasureCategory
                ? "chest"
                : "bust"
            }
            packageDims={{
              packageLength: common.packageLength || "",
              packageBreadth: common.packageBreadth || "",
              packageHeight: common.packageHeight || "",
              volumetricWeightKg: common.volumetricWeightKg || "",
              netWeight: common.netWeight || "",
              quantityInGram: common.quantityInGram || "",
            }}
            onPackageChange={(key, val) =>
              setCommon((c) => ({ ...c, [key]: val }))
            }
          />
        )}
      </div>
      <ShopifyImportModal
        open={shopifyOpen}
        onClose={() => setShopifyOpen(false)}
        onImported={ingestShopifyProducts}
      />
    </AppShell>
  );
}

/* ───────── per-size measurements drawer ───────── */
function MeasurementsDrawer({ sizes, values, onChange, onClose, profile, packageDims, onPackageChange }) {
  // Common Myntra measurement columns per category profile:
  //   "shoe"  → footwear size-chart columns (UK row → US / EURO / foot length)
  //   "chest" → shirts / t-shirts / menswear (chest-first)
  //   "bust"  → women's apparel (kurti / dress — bust + hip first)
  const FIELDS =
    profile === "shoe"
      ? [
          { k: "usSize", label: "US Size" },
          { k: "euroSize", label: "EURO Size" },
          { k: "toFitFootLength", label: "To Fit Foot Length (cm)" },
          { k: "toFitFootWidth", label: "To Fit Foot Width (cm)" },
        ]
      : profile === "chest"
      ? [
        { k: "measureChest", label: "Chest" },
        { k: "measureToFitChest", label: "To Fit Chest" },
        { k: "measureShoulder", label: "Across Shoulder" },
        { k: "measureWaist", label: "Waist" },
        { k: "measureSleeveLength", label: "Sleeve Length" },
        { k: "measureFrontLength", label: "Front Length" },
        { k: "measureLength", label: "Length" },
        { k: "measureInseam", label: "Inseam Length" },
        { k: "measureOutseam", label: "Outseam Length" },
      ]
    : [
        { k: "measureBust", label: "Bust" },
        { k: "measureToFitBust", label: "To Fit Bust" },
        { k: "measureWaist", label: "Waist" },
        { k: "measureToFitWaist", label: "To Fit Waist" },
        { k: "measureHips", label: "Hips" },
        { k: "measureToFitHip", label: "To Fit Hip" },
        { k: "measureFrontLength", label: "Front Length" },
        { k: "measureShoulder", label: "Across Shoulder" },
        { k: "measureSleeveLength", label: "Sleeve Length" },
        { k: "measureLength", label: "Length" },
        { k: "measureGarmentWaist", label: "Garment Waist" },
      ];

  // Package / shipping fields go per-PRODUCT (not per-size). Used by all
  // categories on Myntra — required for shipping label generation.
  const PACKAGE_FIELDS = [
    { k: "packageLength", label: "Length (cm)", placeholder: "30" },
    { k: "packageBreadth", label: "Breadth (cm)", placeholder: "20" },
    { k: "packageHeight", label: "Height (cm)", placeholder: "5" },
    { k: "volumetricWeightKg", label: "Volumetric Weight (kg)", placeholder: "0.4" },
    { k: "netWeight", label: "Net Weight (e.g. 250g)", placeholder: "250g", type: "text" },
    { k: "quantityInGram", label: "Quantity (g)", placeholder: "250" },
  ];

  function setVal(sz, key, val) {
    onChange((cur) => ({
      ...cur,
      [sz]: { ...(cur[sz] || {}), [key]: val },
    }));
  }
  function setPkg(key, val) {
    onPackageChange(key, val);
  }

  return (
    <div
      className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
      data-testid="measures-drawer"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-4xl rounded-2xl bg-white border border-[#EDEDF1] shadow-2xl flex flex-col text-[#282C3F]"
        style={{ maxHeight: "90vh" }}
      >
        <div className="flex items-center justify-between p-5 border-b border-[#EDEDF1]">
          <div>
            <div className="text-base font-bold flex items-center gap-2 text-[#282C3F]">
              <Ruler size={16} className="text-[#4F46E5]" /> Size measurements & package
            </div>
            <div className="text-[11px] text-[#535665] mt-0.5">
              {profile === "shoe"
                ? "UK size → US / EURO / foot length pre-filled from the Myntra chart. Edit any cell to override."
                : "Per-size measurements in inches. Package dimensions are per-product. Skip rows you don't need — empty cells stay blank."}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-[#F5F5F8] border border-[#EDEDF1] text-[#535665]"
            data-testid="close-measures"
          >
            <X size={14} />
          </button>
        </div>

        <div className="p-5 overflow-auto flex-1 space-y-6">
          {/* ─── PER-SIZE TABLE ─── */}
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#94969F] mb-2">
              {profile === "shoe"
                ? "Size chart (UK → US / EURO / foot length cm)"
                : "Per-size measurements (inches)"}
            </div>
            <table className="w-full text-xs">
              <thead className="text-left text-[#535665]">
                <tr>
                  <th className="px-2 py-1.5 sticky left-0 bg-white">
                    {profile === "shoe" ? "UK Size" : "Size"}
                  </th>
                  {FIELDS.map((f) => (
                    <th key={f.k} className="px-2 py-1.5 font-semibold whitespace-nowrap">
                      {f.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sizes.map((sz) => (
                  <tr key={sz} className="border-t border-[#F5F5F8]">
                    <td className="px-2 py-1.5 font-mono-data text-[#282C3F] font-bold sticky left-0 bg-white">
                      {sz}
                    </td>
                    {FIELDS.map((f) => (
                      <td key={f.k} className="px-1 py-1">
                        <input
                          type="number"
                          step="0.5"
                          value={(values[sz] || {})[f.k] || ""}
                          onChange={(e) => setVal(sz, f.k, e.target.value)}
                          data-testid={`measure-${sz}-${f.k}`}
                          className="w-20 px-2 py-1 bg-white border border-[#E9E9ED] rounded text-[#282C3F] focus:outline-none focus:border-[#4F46E5]"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ─── PACKAGE DIMENSIONS (per-product) ─── */}
          <div className="pt-5 border-t border-[#EDEDF1]">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#94969F] mb-1">
              Package & weight (per product · used for shipping)
            </div>
            <div className="text-[11px] text-[#94969F] mb-3">
              Myntra uses these to generate the shipping label. Same values
              applied to every size of this product.
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {PACKAGE_FIELDS.map((f) => (
                <div key={f.k}>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#535665] mb-1">
                    {f.label}
                  </div>
                  <input
                    type={f.type || "number"}
                    step="0.1"
                    placeholder={f.placeholder}
                    value={packageDims[f.k] || ""}
                    onChange={(e) => setPkg(f.k, e.target.value)}
                    data-testid={`pkg-${f.k}`}
                    className="w-full px-2.5 py-1.5 bg-white border border-[#E9E9ED] rounded text-[#282C3F] text-sm focus:outline-none focus:border-[#4F46E5]"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-[#EDEDF1] flex justify-end">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-bold"
            data-testid="save-measures-btn"
          >
            <CheckCircle2 size={14} /> Save
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────── templates button + popover ───────── */
function TemplatesButton({ loadTemplates, applyTemplate }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  async function toggle() {
    if (open) {
      setOpen(false);
      return;
    }
    setBusy(true);
    const list = await loadTemplates();
    setItems(list || []);
    setBusy(false);
    setOpen(true);
  }

  async function remove(id) {
    try {
      await api.delete(`/seller/listing-templates/${id}`);
      setItems((cur) => cur.filter((x) => x.id !== id));
      toast.success("Template deleted");
    } catch (e) {
      toast.error(errMsg(e, "Delete failed"));
    }
  }

  return (
    <div className="relative">
      <button
        onClick={toggle}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/10 text-white/80 text-xs font-semibold"
        data-testid="templates-btn"
      >
        <Bookmark size={12} /> Templates
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-80 z-30 rounded-xl bg-[#1A0F11] border border-white/15 shadow-2xl"
          data-testid="templates-popover"
        >
          <div className="p-3 border-b border-white/10 flex items-center justify-between">
            <div className="text-sm font-bold">Saved listing templates</div>
            <button
              onClick={() => setOpen(false)}
              className="p-1 rounded hover:bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/10 text-white/60"
              data-testid="templates-close"
            >
              <X size={12} />
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {busy && (
              <div className="p-4 text-xs text-white/50 flex items-center gap-2">
                <Loader2 size={12} className="animate-spin" /> Loading…
              </div>
            )}
            {!busy && items.length === 0 && (
              <div className="p-4 text-xs text-white/50">
                No templates saved yet. Click <b>Save as template</b> on Step 5
                to reuse a configuration later.
              </div>
            )}
            {items.map((t) => (
              <div
                key={t.id}
                className="px-3 py-2.5 flex items-center gap-3 hover:bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/5 border-b border-white/5"
                data-testid={`template-row-${t.id}`}
              >
                <button
                  onClick={() => {
                    applyTemplate(t);
                    setOpen(false);
                  }}
                  className="flex-1 text-left"
                >
                  <div className="text-sm font-bold text-white truncate">
                    {t.name}
                  </div>
                  <div className="text-[10px] text-white/45 mt-0.5">
                    {(t.common?.brand || "—")} · {t.common?.articleType || ""}{" "}
                    · {t.sizes?.length || 0} size(s)
                  </div>
                </button>
                <button
                  onClick={() => remove(t.id)}
                  className="p-1.5 rounded hover:bg-red-500/10 text-red-400"
                  data-testid={`template-delete-${t.id}`}
                  title="Delete template"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


/* ───────── defaults drawer ───────── */
function DefaultsDrawer({ common, setCommon, meta, onSave, onClose }) {
  const fieldMap = useMemo(
    () =>
      Object.fromEntries(
        (meta?.common_fields || []).map((f) => [f.key, f])
      ),
    [meta]
  );
  const fields = [
    "manufacturer",
    "packer",
    "importer",
    "countryOfOrigin",
    "hsn",
    "ageGroup",
    "fashionType",
  ]
    .map((k) => fieldMap[k])
    .filter(Boolean);

  return (
    <div
      className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
      data-testid="defaults-drawer"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl bg-white border border-[#E9E9ED] shadow-2xl text-[#282C3F]"
      >
        <div className="flex items-center justify-between p-5 border-b border-[#E9E9ED]">
          <div>
            <div className="text-base font-bold text-[#282C3F]">Seller compliance defaults</div>
            <div className="text-[11px] text-[#535665] mt-0.5">
              Saved once, auto-applied to every listing.
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-[#F5F5F6] border border-[#E9E9ED] text-[#535665]"
            data-testid="close-defaults"
          >
            <X size={14} />
          </button>
        </div>
        <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
          {fields.map((f) => (
            <div key={f.key}>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#535665] mb-1">
                {f.label} {f.required && <span className="text-[#FF3F6C]">*</span>}
              </div>
              {f.options ? (
                <select
                  value={common[f.key] || ""}
                  onChange={(e) =>
                    setCommon((c) => ({ ...c, [f.key]: e.target.value }))
                  }
                  data-testid={`def-${f.key}`}
                  className="w-full px-3 py-2 bg-white border border-[#D4D5D9] rounded-md text-[#282C3F] text-sm focus:outline-none focus:border-[#FF3F6C]"
                >
                  <option value="">Select…</option>
                  {f.options.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  value={common[f.key] || ""}
                  onChange={(e) =>
                    setCommon((c) => ({ ...c, [f.key]: e.target.value }))
                  }
                  placeholder={f.label}
                  data-testid={`def-${f.key}`}
                  className="w-full px-3 py-2 bg-white border border-[#D4D5D9] rounded-md text-[#282C3F] placeholder:text-[#94969F] text-sm focus:outline-none focus:border-[#FF3F6C]"
                />
              )}
            </div>
          ))}
        </div>
        <div className="p-5 border-t border-[#E9E9ED] flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md text-[#535665] hover:text-[#282C3F] text-sm font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold"
            data-testid="save-defaults-drawer"
          >
            <CheckCircle2 size={14} /> Save defaults
          </button>
        </div>
      </div>
    </div>
  );
}
